import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointUp,
  styleConstants,
} from '@paywithextend/components'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { matchPath } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import CloseButton, {
  CloseButtonDesigns,
} from 'src/components/CloseButton/CloseButton'
import Occlusion from 'src/components/Occlusion/Occlusion'
import { closeModal } from 'src/navigation/Lnk'
import { searchUrl } from 'src/navigation/urls'
import { selectIsSidenavLockedOpenFromState } from 'src/redux/selectors/app.selectors'
import { selectFixedElementPushdown } from 'src/redux/selectors/css.selectors'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'
import { FlyoutFadeIn, LeftFlyoutSlideIn } from 'src/style/keyframeUtils'
import { css } from 'styled-components'

export enum LeftFlyoutBodyWidths {
  Search = 'search',
  CspUserDetail = 'cspUserDetail',
}

interface LeftFlyoutProps {
  className?: string
  width?: LeftFlyoutBodyWidths
  onScroll?: (e: React.UIEvent<HTMLElement>) => void
  childRef?: (node: HTMLDivElement) => void
  parentRef?: (node: HTMLDivElement) => void
  noPadding?: boolean
  header?: React.ReactElement
}

const LeftFlyout: React.FC<React.PropsWithChildren<LeftFlyoutProps>> = (
  props,
): React.ReactElement => {
  const dispatch = useDispatch()
  const fixedElementPushdown = useSelector(selectFixedElementPushdown)
  const location = useSelector(selectLocationFromState)
  const sideNavLockedOpen = useSelector(selectIsSidenavLockedOpenFromState)

  const rightShift = sideNavLockedOpen ? 208 : 40
  const { analytics } = useAnalyticsContext()

  return (
    <div
      css={css`
        z-index: 5;
        position: fixed;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;

        ${fixedElementPushdown > 0 &&
        css`
          top: ${fixedElementPushdown}px;
        `};

        ${getBreakpointDown(breakpointSizes.XSM)(css`
          left: 0px;
        `)}
      `}
    >
      <Occlusion
        css={css`
          z-index: 0;
          ${fixedElementPushdown > 0 &&
          css`
            top: ${fixedElementPushdown}px;
          `};

          ${getBreakpointDown(breakpointSizes.XSM)(css`
            display: none;
          `)};
        `}
        isOpen
        onClick={(): void => {
          dispatch(closeModal())
        }}
        fadeIn
      />
      <div
        className={props.className}
        css={css`
          z-index: 1;
          position: absolute;
          background-color: ${styleConstants.colors.white};
          box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.15);
          left: ${rightShift}px;
          height: 100vh;
          overflow: auto;

          ${getBreakpointUp(breakpointSizes.SM)(css`
            ${props.width === LeftFlyoutBodyWidths.Search &&
            css`
              width: 700px;
              ${LeftFlyoutSlideIn(700, rightShift)};
            `}
            ${props.width === LeftFlyoutBodyWidths.CspUserDetail &&
            css`
              width: 500px;
              ${LeftFlyoutSlideIn(500, rightShift)};
            `}
          `)}

          ${getBreakpointDown(breakpointSizes.XSM)(css`
            width: 100vw;
            left: 0px;
            ${FlyoutFadeIn()};
          `)}
        `}
        onScroll={props.onScroll}
        ref={props.parentRef}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            padding: ${styleConstants.heights.leftFlyoutPaddingSMUp}
              ${styleConstants.widths.leftFlyoutPaddingSMUp};

            ${getBreakpointDown(breakpointSizes.XSM)(css`
              padding: ${styleConstants.heights.leftFlyoutPaddingXSMDown}
                ${styleConstants.widths.leftFlyoutPaddingXSMDown};
            `)}

            ${!!props.noPadding &&
            css`
              padding: 0px;
            `}
          `}
          ref={props.childRef}
        >
          {props.header ? (
            <div
              css={css`
                padding: 40px 40px 40px 60px;
                background-color: ${styleConstants.colors.white};
                border-bottom: 1px solid ${styleConstants.colors.gray_medium};
                display: flex;
                flex-direction: column;
              `}
            >
              <CloseButton
                css={css`
                  flex-shrink: 0;
                  align-self: flex-end;
                `}
                design={CloseButtonDesigns.DarkOnLight}
                onClick={(): void => {
                  dispatch(closeModal())
                }}
              />
              {props.header}
            </div>
          ) : (
            <CloseButton
              css={css`
                flex-shrink: 0;
                align-self: flex-end;

                ${props.width === LeftFlyoutBodyWidths.Search &&
                css`
                  top: 40px;
                  right: 60px;
                  ${getBreakpointDown(breakpointSizes.XSM)(css`
                    top: 20px;
                    right: 20px;
                  `)}
                `}
              `}
              design={CloseButtonDesigns.DarkOnLight}
              onClick={(): void => {
                dispatch(closeModal())
                matchPath(location.pathname, searchUrl()) &&
                  analytics?.track(
                    'search flyout - navigate away - button press',
                    {
                      screen: '/search',
                      interaction: 'button press',
                      intent: 'navigate away',
                    },
                  )
              }}
            />
          )}
          {props.children}
        </div>
      </div>
    </div>
  )
}

export default LeftFlyout
